"use client";

import React, { useRef } from "react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";

import {
    FaShip,
    FaPlane,
    FaFacebookF,
    FaTwitter,
    FaLinkedinIn,
} from "react-icons/fa";

import { heroData } from "@/data/heroData";

interface HeroProps {
    pageKey: keyof typeof heroData;
}

export default function InnerHero({ pageKey }: HeroProps) {
    const data = heroData[pageKey];

    const container = useRef<HTMLDivElement>(null);
    const trailRef = useRef<HTMLDivElement>(null);

    const mouse = useRef({ x: 0, y: 0 });
    const last = useRef({ x: 0, y: 0 });

    const isInside = useRef(false);
    const raf = useRef<number>(0);

    const imageIndex = useRef(0);

    const GAP = 180;
    const MAX_IMAGES = 10;

    useGSAP(
        () => {
            const hero = container.current;

            if (!hero) return;

            const createTrail = () => {
                if (!isInside.current) return;

                const distance = Math.hypot(
                    mouse.current.x - last.current.x,
                    mouse.current.y - last.current.y
                );

                if (distance < GAP) return;

                const img = document.createElement("img");

                img.src =
                    data.trailImages[
                    imageIndex.current % data.trailImages.length
                    ];

                img.className =
                    "absolute pointer-events-none object-cover rounded-xl shadow-2xl border border-white/10 grayscale-[40%]";

                gsap.set(img, {
                    width: window.innerWidth < 640 ? 90 : 180,
                    height: window.innerWidth < 640 ? 120 : 240,
                    left: mouse.current.x,
                    top: mouse.current.y,
                    xPercent: -50,
                    yPercent: -50,
                    rotate: gsap.utils.random(-8, 8),
                    zIndex: 5,
                    force3D: true,
                });

                trailRef.current?.appendChild(img);

                gsap.fromTo(
                    img,
                    {
                        opacity: 0,
                        scale: 0.7,
                    },
                    {
                        opacity: 1,
                        scale: 1,
                        duration: 0.35,
                        ease: "power2.out",
                    }
                );

                gsap.to(img, {
                    opacity: 0,
                    y: 60,
                    scale: 0.9,
                    duration: 0.7,
                    delay: 0.8,
                    ease: "power2.in",
                    onComplete: () => img.remove(),
                });

                if (
                    trailRef.current &&
                    trailRef.current.children.length > MAX_IMAGES
                ) {
                    const oldest = trailRef.current.children[0] as HTMLElement;

                    gsap.killTweensOf(oldest);

                    gsap.to(oldest, {
                        opacity: 0,
                        y: 80,
                        scale: 0.85,
                        rotate: gsap.getProperty(oldest, "rotate") as number,
                        duration: 0.45,
                        ease: "power2.in",
                        onComplete: () => oldest.remove(),
                    });
                }

                last.current = { ...mouse.current };
                imageIndex.current++;
            };

            const animate = () => {
                createTrail();
                raf.current = requestAnimationFrame(animate);
            };

            const onMove = (e: MouseEvent) => {
                const rect = hero.getBoundingClientRect();

                mouse.current = {
                    x: e.clientX - rect.left,
                    y: e.clientY - rect.top,
                };
            };

            const onEnter = () => {
                isInside.current = true;
            };

            const onLeave = () => {
                isInside.current = false;

                if (!trailRef.current) return;

                Array.from(trailRef.current.children).forEach((child, i) => {
                    gsap.killTweensOf(child);

                    gsap.to(child, {
                        opacity: 0,
                        y: 70,
                        scale: 0.85,
                        duration: 0.4,
                        delay: i * 0.03,
                        ease: "power2.in",
                        onComplete: () => child.remove(),
                    });
                });
            };

            hero.addEventListener("mouseenter", onEnter);
            hero.addEventListener("mousemove", onMove);
            hero.addEventListener("mouseleave", onLeave);

            raf.current = requestAnimationFrame(animate);

            return () => {
                hero.removeEventListener("mouseenter", onEnter);
                hero.removeEventListener("mousemove", onMove);
                hero.removeEventListener("mouseleave", onLeave);

                if (raf.current) {
                    cancelAnimationFrame(raf.current);
                }
            };
        },
        { scope: container, dependencies: [pageKey] }
    );

    return (
        <section
            ref={container}
            className="relative h-screen overflow-hidden bg-black flex flex-col justify-between text-white"
        >
            {/* Background */}
            <div className="absolute inset-0 z-0 overflow-hidden">
                {data.bgVideo ? (
                    <video
                        autoPlay
                        muted
                        loop
                        playsInline
                        className="h-full w-full object-cover opacity-60"
                    >
                        <source src={data.bgVideo} type="video/mp4" />
                    </video>
                ) : (
                    <div
                        className="absolute inset-0 bg-cover bg-center opacity-60"
                        style={{
                            backgroundImage: `url(${data.bgImage})`,
                        }}
                    />
                )}

                <div className="absolute inset-0 bg-black/45" />
            </div>

            {/* Cursor Trail */}
            <div
                ref={trailRef}
                className="absolute inset-0 z-10 pointer-events-none"
            />

            {/* Content */}
            <div className="relative z-20 flex flex-1 items-center justify-center px-6">
                <div className="text-center max-w-7xl">
                    <h1 className="text-[clamp(3rem,14vw,10rem)] font-black uppercase tracking-tight leading-[0.85]">
                        {data.title}
                    </h1>

                    <div className="mt-6 flex items-center gap-6 max-w-2xl mx-auto">
                        <div className="flex-1 h-px bg-white/20" />

                        <p className="uppercase tracking-[0.6em] text-xs md:text-sm text-white/80">
                            {data.subtitle}
                        </p>

                        <div className="flex-1 h-px bg-white/20" />
                    </div>
                </div>
            </div>

            {/* Footer */}
            <div className="relative z-30 flex flex-col md:flex-row justify-between items-center gap-6 px-6 md:px-12 pb-8">
                <div className="flex gap-4">
                    <SocialCircle>
                        <FaPlane size={14} />
                    </SocialCircle>

                    <SocialCircle>
                        <FaShip size={14} />
                    </SocialCircle>

                    <SocialCircle>
                        <FaLinkedinIn size={14} />
                    </SocialCircle>
                </div>

                <div className="text-center">
                    <p className="uppercase tracking-[0.4em] text-[10px] text-white/40">
                        {data.category}
                    </p>
                </div>

                <div className="flex items-center gap-5">
                    {/* <div className="hidden sm:block text-right">
                        <p className="text-[10px] uppercase text-white/40">
                            HQ Region
                        </p>

                        <p className="tracking-[0.25em] text-xs">
                            {data.location}
                        </p>
                    </div> */}

                    <div className="flex gap-4">
                        <SocialCircle>
                            <FaFacebookF size={14} />
                        </SocialCircle>

                        <SocialCircle>
                            <FaTwitter size={14} />
                        </SocialCircle>
                    </div>
                </div>
            </div>
        </section>
    );
}

interface SocialCircleProps {
    children: React.ReactNode;
}

function SocialCircle({ children }: SocialCircleProps) {
    return (
        <button
            className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center hover:bg-white hover:text-black transition-all duration-300"
            type="button"
        >
            {children}
        </button>
    );
}